import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const CustomerAuthContext = createContext(null);

const STORAGE_TOKEN = 'customer_token';
const STORAGE_USER = 'customer_user';
const STORAGE_DISMISSED = 'customer_login_dismissed';
const SESSION_MARKER = 'session';

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_TOKEN) || '');
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_USER);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_DISMISSED) === '1';
    } catch {
      return false;
    }
  });
  const [authLoading, setAuthLoading] = useState(true);

  const isLoggedIn = !authLoading && Boolean(user && user.phone_number);

  // Rehydrate session: prefer bot_auth token if present, else probe /customer/session.
  useEffect(() => {
    let cancelled = false;
    setAuthLoading(true);

    // Check for bot_auth token in the URL (WhatsApp bot auto-login).
    const urlParams = new URLSearchParams(window.location.search || '');
    const botAuth = urlParams.get('bot_auth');

    const finishLogin = (u) => {
      setUser({ phone_number: u.phone_number, name: u.name });
      setToken(SESSION_MARKER);
      localStorage.setItem(STORAGE_TOKEN, SESSION_MARKER);
      localStorage.setItem(STORAGE_USER, JSON.stringify({ phone_number: u.phone_number, name: u.name }));
    };

    const clearLogin = () => {
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_USER);
      setToken('');
      setUser(null);
    };

    const stripBotAuth = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('bot_auth');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      } catch { /* ignore */ }
    };

    const run = async () => {
      try {
        if (botAuth) {
          // Exchange bot token for a real customer session
          const res = await api.post('/customer/bot-auth', { bot_auth: botAuth });
          if (!cancelled && res.data?.user?.phone_number) {
            finishLogin(res.data.user);
            stripBotAuth();
            return;
          }
        }

        // Fallback: check existing cookie-based session
        const res = await api.get('/customer/session');
        if (cancelled) return;
        const u = res.data?.user || {};
        if (res.data?.logged_in && u.phone_number) {
          finishLogin(u);
        } else {
          clearLogin();
        }
      } catch {
        if (!cancelled) clearLogin();
      } finally {
        if (botAuth) stripBotAuth();
        if (!cancelled) setAuthLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback((_newToken, userData) => {
    localStorage.setItem(STORAGE_TOKEN, SESSION_MARKER);
    localStorage.setItem(STORAGE_USER, JSON.stringify(userData));
    setToken(SESSION_MARKER);
    setUser(userData);
    setDismissed(false);
    try {
      localStorage.removeItem(STORAGE_DISMISSED);
    } catch {
      // ignore
    }
  }, []);

  const logout = useCallback(() => {
    api.post('/auth/logout', { role: 'customer' }).catch(() => {});
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_USER);
    setToken('');
    setUser(null);
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_DISMISSED, '1');
    } catch {
      // ignore
    }
  }, []);

  const undismiss = useCallback(() => {
    setDismissed(false);
    try {
      localStorage.removeItem(STORAGE_DISMISSED);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(() => ({
    token,
    user,
    isLoggedIn,
    authLoading,
    dismissed,
    login,
    logout,
    dismiss,
    undismiss,
  }), [token, user, isLoggedIn, authLoading, dismissed, login, logout, dismiss, undismiss]);

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
}

export default CustomerAuthContext;
